import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  UserPlus, Mail, Copy, Check, X, Loader2, Shield, Clock, 
  Send, Trash2, RefreshCw, Calendar, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInvitations = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    admin_role: 'content_editor',
    expires_in_days: 7
  });

  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isSuperAdmin = adminUser.admin_role === 'super_admin';

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can manage invitations');
      window.location.href = '/admin/dashboard';
      return;
    }
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/invitations/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitations(response.data.invitations);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
      toast.error('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API}/admin/invitations/create`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const fullLink = `${window.location.origin}${response.data.invitation_link}`;
      
      toast.success('Invitation created! Link copied to clipboard.');
      
      // Copy link to clipboard
      navigator.clipboard.writeText(fullLink);
      
      // Reset form
      setFormData({ email: '', admin_role: 'content_editor', expires_in_days: 7 });
      setShowCreateForm(false);
      
      // Refresh list
      fetchInvitations();
    } catch (error) {
      console.error('Failed to create invitation:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create invitation';
      toast.error(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = (token) => {
    const link = `${window.location.origin}/admin/accept-invitation?token=${token}`;
    navigator.clipboard.writeText(link);
    setCopiedToken(token);
    toast.success('Invitation link copied!');
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleRevoke = async (invitationId) => {
    if (!confirm('Are you sure you want to revoke this invitation?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/invitations/${invitationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invitation revoked');
      fetchInvitations();
    } catch (error) {
      console.error('Failed to revoke invitation:', error);
      toast.error('Failed to revoke invitation');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      expired: 'bg-gray-100 text-gray-800',
      revoked: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_admin: 'bg-purple-100 text-purple-800',
      programme_manager: 'bg-blue-100 text-blue-800',
      moderator: 'bg-green-100 text-green-800',
      content_editor: 'bg-amber-100 text-amber-800'
    };
    const labels = {
      super_admin: 'Super Admin',
      programme_manager: 'Programme Manager',
      moderator: 'Moderator',
      content_editor: 'Content Editor'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[role]}`}>
        {labels[role]}
      </span>
    );
  };

  if (!isSuperAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-12 h-12 text-ssdp-teal animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Admin Invitations
          </h1>
          <p className="text-gray-600">
            Invite team members to join as admins
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={fetchInvitations}
            variant="outline"
            className="border-2"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-gradient-to-r from-ssdp-teal to-ssdp-purple text-white"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            Create Invitation
          </Button>
        </div>
      </div>

      {/* Create Invitation Form */}
      {showCreateForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-6 border-2 border-indigo-200"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Invitation</h2>
          <form onSubmit={handleCreateInvitation} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                    placeholder="team@example.com"
                    required
                  />
                </div>
              </div>

              {/* Admin Role */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Admin Role
                </label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={formData.admin_role}
                    onChange={(e) => setFormData({ ...formData, admin_role: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none appearance-none"
                    required
                  >
                    <option value="content_editor">Content Editor</option>
                    <option value="moderator">Moderator</option>
                    <option value="programme_manager">Programme Manager</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Expires In */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Link Expires In
              </label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={formData.expires_in_days}
                  onChange={(e) => setFormData({ ...formData, expires_in_days: parseInt(e.target.value) })}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none appearance-none"
                >
                  <option value={1}>1 day</option>
                  <option value={3}>3 days</option>
                  <option value={7}>7 days (recommended)</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={creating}
                className="bg-gradient-to-r from-ssdp-teal to-ssdp-purple text-white"
              >
                <Send className="w-5 h-5 mr-2" />
                {creating ? 'Creating...' : 'Create Invitation'}
              </Button>
              <Button
                type="button"
                onClick={() => setShowCreateForm(false)}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Invitations List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
        {invitations.length === 0 ? (
          <div className="p-12 text-center">
            <UserPlus className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No invitations yet. Create one to get started!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Expires</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invitations.map((invitation) => (
                  <tr key={invitation.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{invitation.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getRoleBadge(invitation.admin_role)}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(invitation.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(invitation.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(invitation.expires_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {invitation.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => handleCopyLink(invitation.token)}
                              variant="outline"
                              size="sm"
                              className="border-2"
                            >
                              {copiedToken === invitation.token ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              onClick={() => handleRevoke(invitation.id)}
                              variant="outline"
                              size="sm"
                              className="border-2 text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Box */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-xl p-4"
      >
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">How it works:</p>
            <ol className="list-decimal list-inside space-y-1 text-blue-800">
              <li>Create an invitation with the team member's email and desired role</li>
              <li>Copy the invitation link and send it to them via email or messaging</li>
              <li>They'll click the link, set their username and password, and instantly become an admin</li>
              <li>Invitation links expire automatically for security</li>
            </ol>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminInvitations;
